import { GLOBAL_THEME_COLOR } from '@constants/theme';
import { useAppDispatch } from '@core/configureStore';
import { fetchReservationsSilent } from '@core/features/booking/thunks/fetchReservationsSlient.thunk';
import { useOptimisticBookingState, useRealTime } from '@core/hooks';
import { Reservation, ReservationStatus } from '@core/models';
import { bookingApiService } from '@services/service-register';
import moment, { Moment } from 'moment';
import { useCallback, useEffect } from 'react';

export const renderReservationStatusStr = (reservationStatus: ReservationStatus) => {
  return ['Served', 'Waiting', 'Serving', 'Skipped', 'All'][reservationStatus];
};

const getReservationTimeDiff = (date: Moment, now: Moment, reservationDuration: number) => {
  const millisecondDiff = (now.unix() - date.unix()) * 1000;
  if (millisecondDiff > reservationDuration * 1000) return 0;
  return millisecondDiff;
};

export function useClientReservationStateObserver<T>(reservation: Reservation<T>) {
  const { now } = useRealTime();
  const dispatch = useAppDispatch();
  const { bookingSetting } = useOptimisticBookingState();

  useEffect(() => {
    const observe = async (reservation: Reservation<T>) => {
      const reservedDate = moment(reservation.reserved_date);
      switch (reservation.status) {
        case ReservationStatus.InProgress:
          if (
            reservedDate.isBefore(now) &&
            getReservationTimeDiff(reservedDate, now, bookingSetting.duration) === 0
          ) {
            await bookingApiService.finishReservation(reservation.id);
            await dispatch(fetchReservationsSilent());
          }
          break;
        case ReservationStatus.Waiting:
          if (
            reservedDate.isBefore(now) &&
            getReservationTimeDiff(reservedDate, now, bookingSetting.duration) > 0
          ) {
            await bookingApiService.serveReservation(reservation.id);
            await dispatch(fetchReservationsSilent());
          }
          break;
      }
    };
    observe(reservation);
  }, [bookingSetting, reservation]);
}

export function useReservationStatusText<T>() {
  const { now } = useRealTime();
  const { bookingSetting } = useOptimisticBookingState();

  const innerMethod = useCallback(
    (reservation: Reservation<T>) => {
      const reservedDate = moment(reservation.reserved_date);
      switch (reservation.status) {
        case ReservationStatus.Waiting:
          const millisecondDiff = getReservationTimeDiff(
            reservedDate,
            now,
            bookingSetting.duration
          );
          const diffDuration = moment.duration(millisecondDiff);
          if (reservedDate.isBefore(now) && millisecondDiff > 0)
            return diffDuration.seconds() > 60
              ? `Late ${diffDuration.seconds} seconds`
              : `Late ${diffDuration.minutes()} minutes`;
          return 'Waiting';
        case ReservationStatus.InProgress:
          return 'Serving';
        case ReservationStatus.Finished:
          return 'Served';
        case ReservationStatus.Skipped:
          return 'Skipped';
      }
    },
    [bookingSetting, now]
  );
  return innerMethod;
}

export function useReservationStatusColor<T>() {
  const { now } = useRealTime();
  return (reservation: Reservation<T>) => {
    switch (reservation.status) {
      case ReservationStatus.Waiting:
        const reservedDate = moment(reservation.reserved_date);
        if (reservedDate.isBefore(now)) return GLOBAL_THEME_COLOR.$error_color;
        return GLOBAL_THEME_COLOR.$processing_color;
      case ReservationStatus.InProgress:
        return GLOBAL_THEME_COLOR.$highlight_color;
      case ReservationStatus.Finished:
        return GLOBAL_THEME_COLOR.$success_color;
      case ReservationStatus.Skipped:
        return GLOBAL_THEME_COLOR.$dark_text_color;
      default:
        return GLOBAL_THEME_COLOR.$dark_text_color;
    }
  };
}
